import { TruckIcon, OfficeBuildingIcon, CubeIcon, ExclamationCircleIcon, DocumentTextIcon } from '@heroicons/react/outline'
import { RESPONSIBLE_AREA_TONE, DEFAULT_AREA_TONE } from '../constants/fulfillmentErrorStyles'

// The card and the banner talk about the SAME error seconds apart. While each one derived
// on its own what to say and in which colour, the operator read two different instructions
// for a single problem —and a Fiscal error showed purple on top and red below—.
// This is the only derivation: both surfaces consume it.

// One icon per owner, so the card is recognised before it is read.
const AREA_ICONS = {
  customer: OfficeBuildingIcon,
  lastmile: TruckIcon,
  cubbo_ops: CubeIcon,
  tech: ExclamationCircleIcon,
  fiscal: DocumentTextIcon,
}

// What the operator does NOW. It comes from flag_type because that field already encodes the
// kind of action; the error text explains the why. Anything unmapped falls to escalate, which
// is the safe instruction when we cannot name a better one.
const HEADLINE_BY_FLAG = {
  retry: 'retry',
  wait: 'wait',
  fix_address: 'move_on',
  validate_address: 'move_on',
  fix_fiscal: 'move_on',
  fix_shipping: 'move_on',
  fix_order_data: 'move_on',
  // There is nothing to do with this order at the station and nobody to notify either: the
  // operator's job is to move on. The five fix_* reach the same headline, but they all claim
  // someone fixes something; this one claims no more than what there is.
  move_on: 'move_on',
  contact_support: 'escalate',
  escalate: 'escalate',
}

export const areaTone = responsibleArea => RESPONSIBLE_AREA_TONE[responsibleArea] || DEFAULT_AREA_TONE

export const areaIcon = responsibleArea => AREA_ICONS[responsibleArea] || ExclamationCircleIcon

// `waiting` wins over the flag: with the clock running, a "Reintenta ahora" on top and a
// "Reintenta en 54 s" below contradict each other.
export const operatorHeadlineKey = (flagType, { waiting = false } = {}) => (waiting ? 'wait' : HEADLINE_BY_FLAG[flagType] || 'escalate')

export const isRetryableFlag = flagType => flagType === 'retry' || flagType === 'wait'

// The headline, already resolved. Escalating without saying to whom is not an instruction: "Avisa
// a tu supervisor" over a chip that says TECH sends the operator two ways at once. When there
// is an area, the headline names it —which is also who the report button points to—.
export const operatorHeadline = (i18n, { flagType, waiting = false, areaLabel = null } = {}) => {
  const key = operatorHeadlineKey(flagType, { waiting })
  if (key !== 'escalate') return i18n.t(`fulfillment_errors.operator_headline.${key}`)
  return areaLabel
    ? i18n.t('fulfillment_errors.operator_headline.escalate', { area: areaLabel })
    : i18n.t('fulfillment_errors.operator_headline.escalate_no_area')
}

// The backend sends the current attempt and the code's budget. Once the budget is spent the
// error is already born as tech + escalate, so here we only need to stop offering the button.
// Above this number the budget stops being information: these are the codes that get fixed
// at the bench —recount pieces, close a box— and were given a very high cap precisely so
// that working does not exhaust them. "Intento 2 de 100" tells nobody anything; the count
// is only worth showing when it can really run out.
const MAX_VISIBLE_BUDGET = 10

export const retryBudget = ({ retryCount, maxRetries }) => {
  if (maxRetries === null || maxRetries === undefined) return null
  if (maxRetries > MAX_VISIBLE_BUDGET) return null
  const used = retryCount || 0
  const total = maxRetries + 1
  // The backend keeps counting even after the budget is spent; on screen it stops,
  // because "Intento 5 de 4" is not data, it is a bug in the card.
  return { attempt: Math.min(used + 1, total), total, exhausted: used >= maxRetries }
}

// The order's history, sorted and without repeats.
//
// The retry cycle opens one row per round: it closes the active one as retry_failed and creates
// another. Measured in production, an order stuck on AMPM's missing colonia reached 13
// rounds, and the detail showed thirteen identical cards, in ascending order, with the only
// one that matters —the live one— at the very bottom. Thirteen times the same text is not history:
// it is the same sentence copied, and it pushes off screen the DIFFERENT errors the order
// did have.
//
// Only ADJACENT rows are grouped after sorting, not every row with the same code. An error
// that was resolved and happened again hours later is two episodes and must read as
// two: collapsing them into the first would erase that there was a while when the order was fine.
const isSameRow = (a, b) =>
  a.code === b.code &&
  a.error_message === b.error_message &&
  a.responsible_area === b.responsible_area

// Each group carries ALL its rows in `errors`, from newest to oldest, so that
// the card can expand them. Collapsing without being able to expand would trade one problem for another:
// the operator would see "×4" with no way to know when each round happened or how it ended.
//
// `retry_count` from the newest one, not the group size: old rounds may have been
// pruned, and the backend's counter is the one that matches the card's "Intento N de M".
export const groupOrderErrors = (errors = []) => {
  if (!Array.isArray(errors)) return []

  const sorted = [...errors].sort((a, b) => {
    const byDate = new Date(b.created_at) - new Date(a.created_at)
    // Within the same second the id breaks the tie: created_at has one-second resolution and two
    // consecutive rounds fit inside one, which left the order random.
    return byDate !== 0 ? byDate : (b.id || 0) - (a.id || 0)
  })

  return sorted.reduce((groups, error) => {
    const last = groups[groups.length - 1]
    if (last && isSameRow(last.error, error)) {
      last.errors.push(error)
      last.repeats += 1
      last.since = error.created_at
      return groups
    }
    // `error` is the newest in the group, so its error_status is the LIVE state —and it is
    // the one the card paints—. The ones below are retry_failed by definition.
    groups.push({ error, errors: [error], repeats: 1, since: error.created_at })
    return groups
  }, [])
}
